import sFetch from '../../simple-flow/s-fetch';
import { INSTRUMENT_TYPE_MANUAL } from '../../constants/payments-constants';
import urlGetters from './event-service-urls';
import paginationHelper from './pagination-helper';

const { aFetch } = sFetch;

const {
    getAttendeesUrl,
    getCreateOrderUrl,
    getEventDataUrl,
    getEventHoldsUrl,
    getEventInventoryTiersUrl,
    getGuestTokenUrl,
    getInternalMapUrl,
    getMapStatusUrl,
    getOrderUrl,
    getSetPaymentInstructionUrl,
    getSoldSeatInfoUrl,
    getTicketSalesUrl,
    getEventSpecUnitsUrl,
    getUnitsHoldsUrl,
} = urlGetters;

const { mergeAttendees, mergeTicketClasses } = paginationHelper;

const eventServiceFactory = ({ getOrderHeaders }) => {
    const init = async () => true;

    const aFetchEvent = async ({
        eventId,
        cartId,
        includeGroups,
        includeTierColors,
    }) => {
        const orderHeaders = getOrderHeaders();

        const promiseArray = [
            aFetch({
                url: getInternalMapUrl({
                    eventId,
                    includeGroups,
                    includeTierColors,
                }),
                type: 'json',
                options: {
                    credentials: 'include',
                },
            }),
        ];

        if (cartId) {
            promiseArray.push(
                aFetch({
                    url: getAttendeesUrl({ cartId }),
                    type: 'json',
                    options: {
                        headers: orderHeaders,
                        pagination: {
                            enabled: true,
                            fetchAll: true,
                        },
                        credentials: 'include',
                    },
                }),
                aFetch({
                    url: getOrderUrl({ eventId, cartId }),
                    type: 'json',
                    options: {
                        headers: orderHeaders,
                        credentials: 'include',
                    },
                }),
            );
        }

        let results;

        try {
            results = await Promise.all(promiseArray);
        } catch (e) {
            results = [];
        }

        const seatmap = results[0];
        const attendees = mergeAttendees(results[1]);
        const order = results[2];

        return {
            seatmap,
            attendees,
            order,
        };
    };

    const aSetAttendees = async ({
        cartId,
        tickets = [],
        promoCode,
        deliveryMethodOrder,
    }) => {
        if (!cartId) {
            throw new Error('Please provide cartId');
        }
        const body = {
            tickets,
            ...(promoCode ? { promo_code: promoCode } : {}),
            ...(deliveryMethodOrder
                ? { delivery_method_order: deliveryMethodOrder }
                : {}),
        };
        const bodyStr = `${JSON.stringify(body)}\n`;
        const orderHeaders = getOrderHeaders();
        const params = {
            url: getAttendeesUrl({ cartId }),
            type: 'json',
            options: {
                method: 'POST',
                body: bodyStr,
                headers: orderHeaders,
                credentials: 'include',
            },
        };

        const result = await aFetch(params);

        return result;
    };

    const aRetrieveAttendees = async ({ cartId }) => {
        const orderHeaders = getOrderHeaders();
        const params = {
            url: getAttendeesUrl({ cartId }),
            type: 'json',
            options: {
                method: 'GET',
                headers: orderHeaders,
                credentials: 'include',
            },
        };

        const result = await aFetch(params);

        return result;
    };

    const aRetrieveStatus = async ({
        eventId,
        promoCode,
        holdId,
        viewMode,
    }) => {
        const promiseArray = [];

        promiseArray.push(
            aFetch({
                url: getMapStatusUrl({ eventId, viewMode }),
                type: 'json',
            }),
        );

        if (promoCode || holdId) {
            promiseArray.push(
                aFetch({
                    url: getMapStatusUrl({
                        eventId,
                        promoCode,
                        holdId,
                        viewMode,
                    }),
                    type: 'json',
                    options: {
                        credentials: 'include',
                    },
                }),
            );
        }
        let results;

        try {
            results = await Promise.all(promiseArray);
        } catch (e) {
            results = [];
        }

        const mapStatus = results[0];
        const holdStatus = results[1];

        return {
            mapStatus,
            holdStatus,
        };
    };

    const aRetrieveTickets = async ({
        eventId,
        promoCode,
        pos,
        holdId,
        includeHidden,
    }) => {
        const ticketsUrl = getTicketSalesUrl({
            eventId,
            promoCode,
            pos,
            holdId,
            includeHidden,
        });
        let ticketSales;

        try {
            ticketSales = await aFetch({
                url: ticketsUrl,
                type: 'json',
                options: {
                    pagination: {
                        enabled: true,
                        fetchAll: true,
                    },
                    credentials: 'include',
                },
            });
        } catch (e) {
            ticketSales = [];
        }

        const ticketClasses = mergeTicketClasses(ticketSales);

        return {
            ticketClasses,
        };
    };

    const aRetrieveEventData = async ({ eventId }) => {
        let eventInfo = {};

        try {
            eventInfo = await aFetch({
                url: getEventDataUrl({ eventId }),
                type: 'json',
                credentials: 'include',
            });
        } catch (e) {
            eventInfo = {};
        }

        return eventInfo;
    };

    const aGetGuestToken = async ({ eventId }) => {
        const body = JSON.stringify({ event_id: eventId });

        const { guest_token: guestToken } =
            (await aFetch({
                url: getGuestTokenUrl(),
                type: 'json',
                options: {
                    method: 'POST',
                    body,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                },
            })) || {};

        return guestToken;
    };

    const aCreateOrder = async ({ manual, eventId }) => {
        const body = {
            tickets: [],
            application: manual ? 'manual' : 'embedded_web',
            event_id: eventId,
            recipient_invite_token: null,
            waitlist_code: null,
        };

        const bodyStr = `${JSON.stringify(body)}\n`;
        const orderHeaders = {
            ...getOrderHeaders({ excludeOrderToken: true }),
        };

        const params = {
            url: getCreateOrderUrl(),
            type: 'json',
            options: {
                method: 'POST',
                body: bodyStr,
                headers: orderHeaders,
                credentials: 'include',
                retrieveHeaders: true,
            },
        };

        const result = await aFetch(params);

        return result;
    };

    const aRetrieveInventoryTiers = async ({ eventId }) => {
        const orderHeaders = {
            ...getOrderHeaders({ excludeOrderToken: true }),
        };
        let inventoryTiers = [];

        try {
            const eventInfo = await aFetch({
                url: getEventInventoryTiersUrl({ eventId }),
                type: 'json',
                credentials: 'include',
                options: {
                    headers: orderHeaders,
                },
            });

            inventoryTiers = eventInfo['inventory_tiers'];
        } catch (e) {
            inventoryTiers = [];
        }

        return inventoryTiers;
    };

    const aSetPaymentInstruction = async ({
        cartId,
        paymentStatus,
        paymentAmount,
    }) => {
        const orderHeaders = {
            ...getOrderHeaders({ excludeOrderToken: true }),
        };

        const amount =
            paymentAmount?.currency !== '' && paymentAmount?.value !== ''
                ? paymentAmount
                : null;

        const body = {
            payment_methods: [
                {
                    payment_instrument_details: {
                        payment_status: paymentStatus,
                        instrument_type: INSTRUMENT_TYPE_MANUAL,
                    },
                    amount,
                },
            ],
        };
        const bodyStr = `${JSON.stringify(body)}\n`;
        let result;

        try {
            result = await aFetch({
                url: getSetPaymentInstructionUrl({ cartId }),
                type: 'json',
                options: {
                    method: 'POST',
                    body: bodyStr,
                    headers: orderHeaders,
                    credentials: 'include',
                },
            });
        } catch (e) {
            throw new Error();
        }

        return result;
    };

    const aRetrieveHoldsData = async ({ eventId }) => {
        let holdsInfo = {};

        try {
            holdsInfo = await aFetch({
                url: getEventHoldsUrl({ eventId }),
                type: 'json',
                credentials: 'include',
            });
        } catch (e) {
            holdsInfo = {};
        }

        return holdsInfo;
    };

    const aUpdateEventHolds = async ({ eventId, specUnits }) => {
        const orderHeaders = {
            ...getOrderHeaders({ excludeOrderToken: true }),
        };
        const body = {
            units: specUnits,
        };

        try {
            await aFetch({
                url: getEventSpecUnitsUrl({
                    eventId,
                }),
                type: 'json',
                options: {
                    method: 'PUT',
                    body: `${JSON.stringify(body)}\n`,
                    headers: orderHeaders,
                    credentials: 'include',
                },
            });
        } catch (e) {
            throw new Error();
        }
    };

    const aRetrieveSoldSeatInfo = async ({ eventId, seatId }) => {
        let soldSeatInfo;

        try {
            soldSeatInfo = await aFetch({
                url: getSoldSeatInfoUrl({ eventId, seatId }),
                type: 'json',
                credentials: 'include',
            });
        } catch (e) {
            console.error(e);
            throw new Error();
        }

        return soldSeatInfo;
    };

    const aReleaseHold = async ({ eventId, seatId }) => {
        const orderHeaders = {
            ...getOrderHeaders({ excludeOrderToken: true }),
        };
        const body = {
            hold_id: null,
            unit_ids: seatId,
        };

        try {
            await aFetch({
                url: getUnitsHoldsUrl({
                    eventId,
                }),
                type: 'json',
                options: {
                    method: 'PUT',
                    body: JSON.stringify(body),
                    headers: orderHeaders,
                    credentials: 'include',
                },
            });
        } catch (e) {
            console.error(e);
            throw new Error(e);
        }
    };

    return {
        init,
        aFetchEvent,
        aSetAttendees,
        aSetPaymentInstruction,
        aReleaseHold,
        aRetrieveAttendees,
        aRetrieveEventData,
        aRetrieveHoldsData,
        aRetrieveInventoryTiers,
        aRetrieveSoldSeatInfo,
        aRetrieveStatus,
        aRetrieveTickets,
        aGetGuestToken,
        aCreateOrder,
        aUpdateEventHolds,
    };
};

export default eventServiceFactory;
